import React, { useCallback, useMemo, useState } from 'react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import GetStartedContext from './GetStartedContext';
import GetStartedModal from './GetStartedModal';
import EnterpriseEnquiryModal from '../EnterpriseEnquiryModal';
import styles from './MarketingLayout.module.css';
import '../../styles/marketing/tokens.css';
import '../../styles/marketing/base.css';

/**
 * Shared chrome for every public marketing page: header, footer, skip link and
 * the "Get started" flow.
 *
 * The flow has two stages. The chooser asks how the visitor wants to start
 * (a profile, a team, or a card); choosing a custom card or a team hands off
 * to the existing enquiry wizard, which is already wired to
 * POST /api/enterprise/enquiry. Creating a profile goes to /register. The
 * standard card option is the storefront and leaves the site.
 */
const MarketingLayout = ({ children }) => {
  const [stage, setStage] = useState(null);
  const [audience, setAudience] = useState('team');

  const openGetStarted = useCallback(() => setStage('chooser'), []);

  const openEnquiry = useCallback((nextAudience = 'team') => {
    setAudience(nextAudience);
    setStage('enquiry');
  }, []);

  const close = useCallback(() => setStage(null), []);

  const contextValue = useMemo(
    () => ({ openGetStarted, openEnquiry }),
    [openGetStarted, openEnquiry]
  );

  return (
    <GetStartedContext.Provider value={contextValue}>
      <div className={`vxm ${styles.shell}`}>
        <a href="#main-content" className="vxm-skip-link">
          Skip to main content
        </a>
        <SiteHeader />
        <main id="main-content" className={styles.main}>
          {children}
        </main>
        <SiteFooter />

        {stage === 'chooser' && (
          <GetStartedModal onClose={close} onChooseEnquiry={openEnquiry} />
        )}
        {stage === 'enquiry' && (
          <EnterpriseEnquiryModal audience={audience} onClose={close} />
        )}
      </div>
    </GetStartedContext.Provider>
  );
};

export default MarketingLayout;
